// src/types.ts
var Verdict = /* @__PURE__ */ ((Verdict2) => {
  Verdict2["VERIFIED"] = "VERIFIED";
  Verdict2["VERIFIED_CONTRADICTION"] = "VERIFIED_CONTRADICTION";
  Verdict2["UNKNOWN"] = "UNKNOWN";
  Verdict2["SUSPICIOUS"] = "SUSPICIOUS";
  return Verdict2;
})(Verdict || {});

// src/constants.ts
var WELL_KNOWN_NEVER_TOKENS = [
  { token: "payment", description: "We never request payment from candidates" },
  { token: "crypto", description: "We never request cryptocurrency transactions" },
  { token: "gift-cards", description: "We never request gift card purchases" },
  { token: "personal-email", description: "We never recruit from personal email addresses" },
  { token: "whatsapp-first-contact", description: "We never initiate first contact via WhatsApp" },
  { token: "telegram-first-contact", description: "We never initiate first contact via Telegram" },
  { token: "id-before-offer", description: "We never request government ID before a formal offer" },
  { token: "bank-details-before-offer", description: "We never request bank/financial details before a formal offer" }
];
var WELL_KNOWN_NEVER_SET = new Set(
  WELL_KNOWN_NEVER_TOKENS.map((t) => t.token)
);
var DEFAULT_NEVER_TOKENS = /* @__PURE__ */ new Set([
  "payment",
  "crypto",
  "gift-cards",
  "personal-email",
  "id-before-offer"
]);
var NEVER_TOKEN_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
var DOMAIN_PATTERN = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/i;
var HTTPS_PATTERN = /^https:\/\/.+/i;
var CONTACT_PATTERN = /^(mailto:.+@.+\..+|https:\/\/.+)/i;
var COMMA_SAFE_FIELDS = /* @__PURE__ */ new Set([
  "Canonical-Domains",
  "Never"
]);
var KEY_TO_PROPERTY = {
  "tier": "tier",
  "canonical-domains": "canonicalDomains",
  "recruiting-channels": "recruitingChannels",
  "application-urls": "applicationUrls",
  "never": "never",
  "verify-contact": "verifyContact",
  "report-abuse": "reportAbuse",
  "policy": "policy",
  "verified-recruiters": "verifiedRecruiters",
  "expires": "expires",
  "last-updated": "lastUpdated"
};
var ARRAY_FIELDS = /* @__PURE__ */ new Set([
  "canonicalDomains",
  "recruitingChannels",
  "applicationUrls",
  "never",
  "verifiedRecruiters"
]);

// src/parser.ts
function parse(raw) {
  const warnings = [];
  const errors = [];
  const acc = {};
  const lines = raw.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trimStart();
    if (trimmed.startsWith("#")) continue;
    if (trimmed.trim() === "") continue;
    const colonIdx = trimmed.indexOf(":");
    if (colonIdx < 0) {
      warnings.push(`Line ${i + 1}: no key-value separator found, skipping`);
      continue;
    }
    const rawKey = trimmed.slice(0, colonIdx).trim();
    const rawValue = trimmed.slice(colonIdx + 1).trim();
    if (!rawKey) {
      warnings.push(`Line ${i + 1}: empty key, skipping`);
      continue;
    }
    const normalizedKey = rawKey.toLowerCase();
    const prop = KEY_TO_PROPERTY[normalizedKey];
    if (!prop) {
      warnings.push(`Line ${i + 1}: unknown key "${rawKey}", skipping`);
      continue;
    }
    if (!rawValue) {
      warnings.push(`Line ${i + 1}: empty value for "${rawKey}", skipping`);
      continue;
    }
    const wireKey = Object.keys(KEY_TO_PROPERTY).find(
      (k) => k.toLowerCase() === normalizedKey
    );
    let values;
    if (ARRAY_FIELDS.has(prop) && COMMA_SAFE_FIELDS.has(capitalizeWireKey(wireKey))) {
      values = rawValue.split(",").map((s) => s.trim()).filter(Boolean);
    } else {
      values = [rawValue];
    }
    if (!acc[prop]) acc[prop] = [];
    acc[prop].push(...values);
  }
  const data = {};
  for (const field of ARRAY_FIELDS) {
    if (acc[field]) {
      data[field] = dedupe(acc[field]);
    }
  }
  const scalarFields = [
    "tier",
    "verifyContact",
    "reportAbuse",
    "policy",
    "expires",
    "lastUpdated"
  ];
  for (const field of scalarFields) {
    if (acc[field]) {
      const val = acc[field][acc[field].length - 1];
      if (field === "tier") {
        const num = parseInt(val, 10);
        if (num === 0 || num === 1 || num === 2) {
          data.tier = num;
        } else {
          warnings.push(`Invalid tier value "${val}", ignoring`);
        }
      } else {
        data[field] = val;
      }
    }
  }
  const required = [
    "canonicalDomains",
    "recruitingChannels",
    "applicationUrls",
    "never",
    "verifyContact",
    "reportAbuse",
    "expires"
  ];
  for (const field of required) {
    const value = data[field];
    if (value === void 0 || value === "" || Array.isArray(value) && value.length === 0) {
      errors.push(`Missing required field: ${field}`);
    }
  }
  if (errors.length > 0) {
    return { ok: false, errors, warnings };
  }
  return { ok: true, data, warnings };
}
function dedupe(arr) {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const item of arr) {
    if (!seen.has(item)) {
      seen.add(item);
      result.push(item);
    }
  }
  return result;
}
function capitalizeWireKey(key) {
  return key.split("-").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join("-");
}

// src/serializer.ts
function serialize(data) {
  const lines = [];
  const host = data.canonicalDomains[0] || "example.com";
  lines.push(`# https://${host}/.well-known/hiring.txt`);
  if (data.canonicalDomains.length > 0) {
    lines.push(`Canonical-Domains: ${data.canonicalDomains.join(", ")}`);
  }
  for (const url of data.recruitingChannels) {
    lines.push(`Recruiting-Channels: ${url}`);
  }
  for (const url of data.applicationUrls) {
    lines.push(`Application-URLs: ${url}`);
  }
  if (data.never.length > 0) {
    lines.push(`Never: ${data.never.join(", ")}`);
  }
  if (data.verifyContact) {
    lines.push(`Verify-Contact: ${data.verifyContact}`);
  }
  if (data.reportAbuse) {
    lines.push(`Report-Abuse: ${data.reportAbuse}`);
  }
  if (data.policy) {
    lines.push(`Policy: ${data.policy}`);
  }
  if (data.verifiedRecruiters) {
    for (const url of data.verifiedRecruiters) {
      lines.push(`Verified-Recruiters: ${url}`);
    }
  }
  if (data.expires) {
    lines.push(`Expires: ${data.expires}`);
  }
  const lastUpdated = data.lastUpdated || (/* @__PURE__ */ new Date()).toISOString().replace(/\.\d{3}Z$/, "Z");
  lines.push(`Last-Updated: ${lastUpdated}`);
  return lines.join("\n") + "\n";
}

// src/validator.ts
function validate(data) {
  const errors = [];
  if (!data.canonicalDomains || data.canonicalDomains.length === 0) {
    errors.push({ field: "canonicalDomains", message: "At least one canonical domain is required", severity: "error" });
  }
  if (!data.recruitingChannels || data.recruitingChannels.length === 0) {
    errors.push({ field: "recruitingChannels", message: "At least one recruiting channel is required", severity: "error" });
  }
  if (!data.applicationUrls || data.applicationUrls.length === 0) {
    errors.push({ field: "applicationUrls", message: "At least one application URL is required", severity: "error" });
  }
  if (!data.never || data.never.length === 0) {
    errors.push({ field: "never", message: "At least one Never assertion is required", severity: "error" });
  }
  if (!data.verifyContact) {
    errors.push({ field: "verifyContact", message: "Verify contact is required", severity: "error" });
  }
  if (!data.reportAbuse) {
    errors.push({ field: "reportAbuse", message: "Report abuse contact is required", severity: "error" });
  }
  if (!data.expires) {
    errors.push({ field: "expires", message: "Expiration date is required", severity: "error" });
  }
  if (data.canonicalDomains) {
    for (const domain of data.canonicalDomains) {
      if (!DOMAIN_PATTERN.test(domain)) {
        errors.push({ field: "canonicalDomains", message: `Invalid domain format: "${domain}"`, severity: "error" });
      }
    }
  }
  if (data.recruitingChannels) {
    for (const url of data.recruitingChannels) {
      if (!HTTPS_PATTERN.test(url)) {
        errors.push({ field: "recruitingChannels", message: `Must be HTTPS: "${url}"`, severity: "error" });
      }
    }
  }
  if (data.applicationUrls) {
    for (const url of data.applicationUrls) {
      if (!HTTPS_PATTERN.test(url)) {
        errors.push({ field: "applicationUrls", message: `Must be HTTPS: "${url}"`, severity: "error" });
      }
    }
  }
  if (data.never) {
    for (const token of data.never) {
      if (!NEVER_TOKEN_PATTERN.test(token)) {
        errors.push({ field: "never", message: `Invalid token format: "${token}" (must be kebab-case)`, severity: "error" });
      }
    }
  }
  if (data.verifyContact && !CONTACT_PATTERN.test(data.verifyContact)) {
    errors.push({ field: "verifyContact", message: "Must be mailto: or https:// URI", severity: "error" });
  }
  if (data.reportAbuse && !CONTACT_PATTERN.test(data.reportAbuse)) {
    errors.push({ field: "reportAbuse", message: "Must be mailto: or https:// URI", severity: "error" });
  }
  if (data.policy && !HTTPS_PATTERN.test(data.policy)) {
    errors.push({ field: "policy", message: "Must be HTTPS", severity: "error" });
  }
  if (data.verifiedRecruiters) {
    for (const url of data.verifiedRecruiters) {
      if (!HTTPS_PATTERN.test(url)) {
        errors.push({ field: "verifiedRecruiters", message: `Must be HTTPS: "${url}"`, severity: "error" });
      }
    }
  }
  if (data.expires) {
    const expiresDate = new Date(data.expires);
    if (isNaN(expiresDate.getTime())) {
      errors.push({ field: "expires", message: "Invalid date-time format", severity: "error" });
    }
  }
  if (data.expires) {
    const expiresDate = new Date(data.expires);
    if (!isNaN(expiresDate.getTime()) && expiresDate < /* @__PURE__ */ new Date()) {
      errors.push({ field: "expires", message: "Expiration date is in the past", severity: "warning" });
    }
  }
  return errors;
}
function isValid(data) {
  return validate(data).filter((e) => e.severity === "error").length === 0;
}

// src/heuristics.ts
var FREE_MAIL_PROVIDERS = /* @__PURE__ */ new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "msn.com",
  "aol.com",
  "mail.com",
  "protonmail.com",
  "proton.me",
  "icloud.com",
  "me.com",
  "yandex.com",
  "yandex.ru",
  "zoho.com",
  "gmx.com",
  "gmx.de",
  "web.de",
  "tutanota.com",
  "tuta.io",
  "fastmail.com",
  "mail.ru",
  "inbox.ru",
  "list.ru"
]);
var SCAM_PATTERNS = [
  { pattern: /\b(payment|pay\s*fee|processing\s*fee|registration\s*fee|advance\s*fee)\b/i, type: "payment-language", description: "Payment or fee request detected" },
  { pattern: /\b(bitcoin|btc|ethereum|eth|crypto|usdt|cryptocurrency|wallet\s*address)\b/i, type: "crypto-language", description: "Cryptocurrency reference detected" },
  { pattern: /\b(gift\s*card|itunes\s*card|google\s*play\s*card|prepaid\s*card|steam\s*card)\b/i, type: "gift-card-language", description: "Gift card reference detected" },
  { pattern: /\b(urgent|immediately|within\s*24\s*hours|act\s*now|limited\s*time|expire\s*soon|asap)\b/i, type: "urgency-language", description: "Urgency pressure language detected" },
  { pattern: /\b(wire\s*transfer|western\s*union|moneygram|bank\s*transfer|routing\s*number)\b/i, type: "wire-transfer-language", description: "Wire transfer reference detected" },
  { pattern: /\b(ssn|social\s*security|passport\s*number|national\s*id|identity\s*card)\b/i, type: "id-request-language", description: "Personal identification request detected" },
  { pattern: /\b(guaranteed\s*job|100%\s*placement|no\s*interview\s*needed|instant\s*hire)\b/i, type: "too-good", description: "Unrealistic job guarantee detected" },
  { pattern: /\b(whatsapp\s*me|contact\s*on\s*telegram|text\s*me\s*on|add\s*me\s*on\s*whatsapp)\b/i, type: "messaging-redirect", description: "Redirect to personal messaging app detected" }
];
var CONFUSABLES = {
  "\u0430": "a",
  "\u0435": "e",
  "\u043E": "o",
  "\u0440": "p",
  "\u0441": "c",
  "\u0443": "y",
  "\u0445": "x",
  "\u0456": "i",
  "\u0458": "j",
  "\u0455": "s",
  "\u0501": "d",
  "\u0261": "g",
  "\u0269": "l",
  "0": "o",
  "1": "l",
  "\u0131": "i",
  "\u027E": "r",
  "\u0578": "n",
  "\u03BD": "v",
  "\u03C9": "w",
  "\u03C1": "p",
  "\u03C4": "t",
  "\u2113": "l"
};
function normalizeConfusables(str) {
  return [...str].map((ch) => CONFUSABLES[ch] ?? ch).join("");
}
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}
function detectLookalike(senderDomain, canonicalDomains) {
  const normalizedSender = normalizeConfusables(senderDomain.toLowerCase());
  for (const canonical of canonicalDomains) {
    const normalizedCanonical = normalizeConfusables(canonical.toLowerCase());
    if (normalizedSender === normalizedCanonical && senderDomain.toLowerCase() !== canonical.toLowerCase()) {
      return {
        type: "homograph-domain",
        description: `Domain "${senderDomain}" uses Unicode characters that mimic "${canonical}"`,
        confidence: 0.95
      };
    }
    const dist = levenshtein(normalizedSender, normalizedCanonical);
    if (dist > 0 && dist <= 2 && normalizedSender !== normalizedCanonical) {
      return {
        type: "lookalike-domain",
        description: `Domain "${senderDomain}" is suspiciously similar to "${canonical}" (edit distance: ${dist})`,
        confidence: Math.max(0.6, 1 - dist * 0.2)
      };
    }
  }
  return null;
}
function detectFreeMail(email) {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return null;
  if (FREE_MAIL_PROVIDERS.has(domain)) {
    return {
      type: "free-mail",
      description: `Sender uses free email provider "${domain}" \u2014 legitimate corporate recruiters use company domains`,
      confidence: 0.7
    };
  }
  return null;
}
function detectScamLanguage(messageText) {
  const signals = [];
  for (const { pattern, type, description } of SCAM_PATTERNS) {
    if (pattern.test(messageText)) {
      signals.push({ type, description, confidence: 0.65 });
    }
  }
  return signals;
}
function analyzeHeuristics(input) {
  const signals = [];
  if (input.senderEmail) {
    const freeMail = detectFreeMail(input.senderEmail);
    if (freeMail) signals.push(freeMail);
  }
  const domain = input.senderDomain ?? input.senderEmail?.split("@")[1];
  if (domain && input.canonicalDomains?.length) {
    const lookalike = detectLookalike(domain, input.canonicalDomains);
    if (lookalike) signals.push(lookalike);
  }
  if (input.messageText) {
    signals.push(...detectScamLanguage(input.messageText));
  }
  return signals;
}

// src/email-auth.ts
function parseAuthenticationResults(headerValue) {
  let value = headerValue;
  const headerPrefix = /^authentication-results:\s*/i;
  if (headerPrefix.test(value)) {
    value = value.replace(headerPrefix, "");
  }
  value = value.replace(/\r?\n\s+/g, " ").trim();
  const result = {
    dkim: "none",
    spf: "none",
    dmarc: "none",
    raw: headerValue
  };
  const dkimMatch = value.match(/\bdkim\s*=\s*(pass|fail|temperror|permerror|neutral|none|softfail|policy)\b/i);
  if (dkimMatch) {
    result.dkim = normalizeVerdict(dkimMatch[1]);
  }
  const spfMatch = value.match(/\bspf\s*=\s*(pass|fail|temperror|permerror|neutral|none|softfail)\b/i);
  if (spfMatch) {
    result.spf = normalizeVerdict(spfMatch[1]);
  }
  const dmarcMatch = value.match(/\bdmarc\s*=\s*(pass|fail|temperror|permerror|none|bestguesspass)\b/i);
  if (dmarcMatch) {
    result.dmarc = normalizeVerdict(dmarcMatch[1]);
  }
  return result;
}
function normalizeVerdict(raw) {
  const lower = raw.toLowerCase();
  if (lower === "pass" || lower === "bestguesspass") return "pass";
  if (["fail", "softfail", "permerror", "policy"].includes(lower)) return "fail";
  return "none";
}

// src/verifier.ts
function lookupCompany(nameOrDomain, registry) {
  const query = nameOrDomain.toLowerCase().trim();
  if (!query) return null;
  for (const company of registry) {
    for (const name of company.names) {
      if (name.toLowerCase() === query) return company;
    }
  }
  for (const company of registry) {
    for (const domain of company.canonicalDomains) {
      if (domain.toLowerCase() === query) return company;
    }
  }
  for (const company of registry) {
    for (const name of company.names) {
      if (name.toLowerCase().includes(query) || query.includes(name.toLowerCase())) {
        return company;
      }
    }
  }
  return null;
}
function verify(input, hiringTxtRaw, registryEntry) {
  const steps = [];
  const reasons = [];
  let tier0;
  let heuristics = [];
  let emailAuth;
  const senderDomain = input.senderEmail?.split("@")[1]?.toLowerCase();
  const canonicalDomains = registryEntry?.canonicalDomains ?? (input.canonicalDomain ? [input.canonicalDomain] : []);
  if (registryEntry) {
    steps.push({
      id: "registry",
      status: "pass",
      label: "Company found in registry",
      detail: `${registryEntry.names[0]} \u2014 ${registryEntry.canonicalDomains.join(", ")}`
    });
  } else if (input.canonicalDomain) {
    steps.push({
      id: "registry",
      status: "warn",
      label: "Company not in registry",
      detail: `Using user-provided domain: ${input.canonicalDomain}`
    });
  } else {
    steps.push({
      id: "registry",
      status: "fail",
      label: "Company not found",
      detail: "No matching company in registry and no canonical domain provided"
    });
    return {
      verdict: "UNKNOWN",
      steps,
      heuristics: [],
      reasons: ["Could not resolve canonical domain"]
    };
  }
  if (hiringTxtRaw === null) {
    steps.push({
      id: "fetch",
      status: "fail",
      label: "hiring.txt not accessible",
      detail: "Could not fetch file \u2014 CORS blocked or file does not exist"
    });
    reasons.push("hiring.txt not accessible");
  } else {
    steps.push({
      id: "fetch",
      status: "pass",
      label: "hiring.txt retrieved"
    });
    const parseResult = parse(hiringTxtRaw);
    if (!parseResult.ok) {
      steps.push({
        id: "parse",
        status: "fail",
        label: "Parse failed",
        detail: parseResult.errors.join("; ")
      });
      reasons.push("hiring.txt parse failed");
    } else {
      tier0 = parseResult.data;
      steps.push({
        id: "parse",
        status: "pass",
        label: "Valid hiring.txt parsed"
      });
      const validationErrors = validate(tier0);
      const errors = validationErrors.filter((e) => e.severity === "error");
      if (errors.length > 0) {
        steps.push({
          id: "validate",
          status: "fail",
          label: "Validation failed",
          detail: errors.map((e) => e.message).join("; ")
        });
        reasons.push("hiring.txt validation failed");
      } else {
        steps.push({
          id: "validate",
          status: "pass",
          label: "Tier 0 validation passed"
        });
        const expiresDate = new Date(tier0.expires);
        if (expiresDate.getTime() < Date.now()) {
          steps.push({
            id: "expires",
            status: "fail",
            label: "File expired",
            detail: `Expired on ${tier0.expires}`
          });
          reasons.push("hiring.txt has expired");
        } else {
          steps.push({
            id: "expires",
            status: "pass",
            label: "File is current",
            detail: `Expires: ${tier0.expires}`
          });
        }
        if (senderDomain) {
          const domainMatch = tier0.canonicalDomains.some(
            (d) => d.toLowerCase() === senderDomain
          );
          if (domainMatch) {
            steps.push({
              id: "domain-match",
              status: "pass",
              label: "Sender domain matches declaration",
              detail: `${senderDomain} \u2208 Canonical-Domains`
            });
          } else {
            steps.push({
              id: "domain-match",
              status: "fail",
              label: "Sender domain not declared",
              detail: `${senderDomain} \u2209 {${tier0.canonicalDomains.join(", ")}}`
            });
            reasons.push(`Sender domain "${senderDomain}" not in declared Canonical-Domains`);
          }
        }
        if (input.listingUrl) {
          const channelMatch = tier0.recruitingChannels.some(
            (ch) => input.listingUrl.toLowerCase().startsWith(ch.toLowerCase())
          ) || tier0.applicationUrls.some(
            (url) => input.listingUrl.toLowerCase().startsWith(url.toLowerCase())
          );
          if (channelMatch) {
            steps.push({
              id: "channel-match",
              status: "pass",
              label: "Listing URL matches declared channels"
            });
          } else {
            steps.push({
              id: "channel-match",
              status: "warn",
              label: "Listing URL not in declared channels",
              detail: input.listingUrl
            });
          }
        }
      }
    }
  }
  heuristics = analyzeHeuristics({
    senderEmail: input.senderEmail,
    senderDomain,
    canonicalDomains,
    messageText: input.messageText
  });
  if (heuristics.length > 0) {
    for (const signal of heuristics) {
      steps.push({
        id: `heuristic-${signal.type}`,
        status: "warn",
        label: signal.description,
        detail: `Confidence: ${Math.round(signal.confidence * 100)}%`
      });
    }
    reasons.push(...heuristics.map((s) => s.description));
  }
  if (input.authHeader) {
    emailAuth = parseAuthenticationResults(input.authHeader);
    const allPass = emailAuth.dkim === "pass" && emailAuth.spf === "pass" && emailAuth.dmarc === "pass";
    const anyFail = emailAuth.dkim === "fail" || emailAuth.spf === "fail" || emailAuth.dmarc === "fail";
    steps.push({
      id: "email-auth",
      status: allPass ? "pass" : anyFail ? "fail" : "info",
      label: `Email auth: DKIM=${emailAuth.dkim}, SPF=${emailAuth.spf}, DMARC=${emailAuth.dmarc}`
    });
    if (anyFail) {
      reasons.push("Email authentication failed \u2014 possible spoofing");
    }
  }
  const verdict = determineVerdict(steps, heuristics, tier0);
  return { verdict, steps, tier0, heuristics, emailAuth, reasons };
}
function determineVerdict(steps, heuristics, tier0) {
  const hasHighConfidenceSignal = heuristics.some((s) => s.confidence >= 0.8);
  const criticalSteps = ["fetch", "parse", "validate", "expires"];
  const hasCriticalFail = steps.some((s) => criticalSteps.includes(s.id) && s.status === "fail");
  const domainMatch = steps.find((s) => s.id === "domain-match");
  const domainPassed = domainMatch?.status === "pass";
  if (hasHighConfidenceSignal) {
    return "SUSPICIOUS";
  }
  if (heuristics.length >= 2) {
    return "SUSPICIOUS";
  }
  if (hasCriticalFail) {
    return "UNKNOWN";
  }
  if (domainPassed && tier0 && heuristics.length === 0) {
    return "VERIFIED";
  }
  return "UNKNOWN";
}
export {
  ARRAY_FIELDS,
  COMMA_SAFE_FIELDS,
  CONTACT_PATTERN,
  DEFAULT_NEVER_TOKENS,
  DOMAIN_PATTERN,
  HTTPS_PATTERN,
  KEY_TO_PROPERTY,
  NEVER_TOKEN_PATTERN,
  Verdict as VerdictEnum,
  WELL_KNOWN_NEVER_SET,
  WELL_KNOWN_NEVER_TOKENS,
  analyzeHeuristics,
  detectFreeMail,
  detectLookalike,
  detectScamLanguage,
  isValid,
  lookupCompany,
  parse,
  parseAuthenticationResults,
  serialize,
  validate,
  verify
};
//# sourceMappingURL=index.js.map